import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useApiClient } from '../api';
import type { LocationDtoV3 } from '../../generated-api';
import { useGetLocationsForCurrentUserV2 } from '@ordr-as/ordr-service-types/epos';
import { isValidLocationId } from '../utils/locationId';

const LOCATION_ID_CACHE_KEY = 'favrit.epos.locationIdMapping';

function getCachedNumericId(locationId: string): number | undefined {
  try {
    const cached = localStorage.getItem(`${LOCATION_ID_CACHE_KEY}.${locationId}`);
    if (!cached) {
      return undefined;
    }
    const parsed = Number.parseInt(cached, 10);
    // Validate: must be a positive integer (handles NaN, 0, negative)
    return parsed > 0 ? parsed : undefined;
  } catch {
    return undefined;
  }
}

function setCachedNumericId(locationId: string, numericId: number) {
  try {
    localStorage.setItem(`${LOCATION_ID_CACHE_KEY}.${locationId}`, numericId.toString());
  } catch {
    // Ignore storage errors
  }
}

export function useCachedLocationId(locationId?: string): number | undefined {
  return locationId ? getCachedNumericId(locationId) : undefined;
}

export function useNumericLocationId(locationId?: string, location?: LocationDtoV3): number | undefined {
  const cachedNumericId = useCachedLocationId(locationId);
  return location?.id ?? cachedNumericId;
}

export function useQueryLocations() {
  const { queryKey: _queryKey, ...result } = useGetLocationsForCurrentUserV2({
    query: {
      staleTime: 10 * 60 * 1000, // 10 min
    },
  });
  return result;
}

const LOCATION_META = { persist: true } as const;

export const useQueryLocation = (id?: string) => {
  const apiClient = useApiClient();
  const queryClient = useQueryClient();

  return useQuery({
    enabled: isValidLocationId(id),
    meta: LOCATION_META,

    queryFn: async () => {
      try {
        const data = await apiClient.LocationControllerApi.locationByIdv6(
          { id: id! } // eslint-disable-line @typescript-eslint/no-non-null-assertion
        );

        if (id && data.id) {
          setCachedNumericId(id, data.id);
        }

        return data;
      } catch (error) {
        const cached = queryClient.getQueryData<LocationDtoV3>(['location', id]);
        if (cached) return cached;
        throw error;
      }
    },

    queryKey: ['location', id],

    refetchOnMount: true,
  });
};
